import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import api from "../lib/api";

const CartContext = createContext(null);
const KEY = "hp_cart";

export function CartProvider({ children }) {
    const [items, setItems] = useState(() => {
        try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; }
    });
    const firstRun = useRef(true);

    useEffect(() => { localStorage.setItem(KEY, JSON.stringify(items)); }, [items]);

    // Mirror the cart to the backend (debounced) so abandonment can be detected.
    // Only for signed-in shoppers — there's nobody to email otherwise.
    useEffect(() => {
        if (!localStorage.getItem("hp_token")) return;
        if (firstRun.current && items.length === 0) { firstRun.current = false; return; }
        firstRun.current = false;
        const t = setTimeout(() => {
            api.post("/cart/track", {
                items: items.map((i) => ({ product_id: i.product_id, size_id: i.size_id, quantity: i.quantity })),
            }).catch(() => { });
        }, 2000);
        return () => clearTimeout(t);
    }, [items]);

    const addItem = (product, variant, quantity = 1) => {
        setItems((cur) => {
            const idx = cur.findIndex((i) => i.product_id === product.id && i.size_id === variant.size_id);
            if (idx >= 0) {
                const copy = [...cur];
                copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + quantity };
                return copy;
            }
            return [
                ...cur,
                {
                    product_id: product.id,
                    product_name: product.name,
                    product_slug: product.slug,
                    size_id: variant.size_id,
                    size_label: variant.size_label,
                    price: variant.price,
                    quantity,
                    image: product.images?.[0] || "",
                },
            ];
        });
    };

    const updateQty = (product_id, size_id, quantity) => {
        setItems((cur) => cur.map((i) => (i.product_id === product_id && i.size_id === size_id ? { ...i, quantity } : i)).filter((i) => i.quantity > 0));
    };

    const removeItem = (product_id, size_id) => {
        setItems((cur) => cur.filter((i) => !(i.product_id === product_id && i.size_id === size_id)));
    };

    const clear = () => setItems([]);

    const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
    const count = items.reduce((s, i) => s + i.quantity, 0);

    return (
        <CartContext.Provider value={{ items, addItem, updateQty, removeItem, clear, subtotal, count }}>
            {children}
        </CartContext.Provider>
    );
}

export const useCart = () => useContext(CartContext);
